import React, { Fragment, FunctionComponent } from 'react'
import {
  FieldLabel,
  FieldValue,
  Header,
  Icon,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import { RegisterClosurePrintData } from '../../util/register-closure-types'
import { usePaymentManager, useUserManager } from '../../ng-compat/hooks'
import { useFormatNumber } from '../helpers/formatNumberHooks'
import { RegisterDetails } from './RegisterDetails'

interface PrintRegisterClosureProps {
  data: RegisterClosurePrintData
}

const CashMovements: FunctionComponent<PrintRegisterClosureProps> = ({
  data,
}) => {
  const user = useUserManager().getLoggedInUser()
  const formatNumber = useFormatNumber()

  return (
    <div className="wr-register-closure-print-movements-container">
      <div className="vd-flex vd-flex--justify-between">
        <div>
          <T _str="Cash movements" />
        </div>
        <div className="wr-register-closure-print-values-container wr-register-closure-print-values-container--left">
          <div className="wr-register-closure-print-value">
            {data.cashManagement.isCashFloatSet ? '' : 'Not Set'}
          </div>
        </div>
      </div>
      <div className="wr-register-closure-print-movements-container">
        <div className="wr-register-closure-print-heading">
          <div>
            <T _str="User" />
          </div>
          <div className="wr-register-closure-print-values-container">
            <div className="wr-register-closure-print-value">
              <T _str="Amount" />
            </div>
            <div className="wr-register-closure-print-value wr-register-closure-print-value--text">
              <T _str="Type" />
            </div>
          </div>
        </div>
        <div className="wr-register-closure-print-movements">
          {data.registerClosure.cashMovements.map(cashMovement => (
            <div
              key={cashMovement.id}
              className="wr-register-closure-print-movement vd-flex vd-flex--justify-between"
            >
              <div>{user.display_name || user.username}</div>
              <div className="wr-register-closure-print-values-container">
                <div className="wr-register-closure-print-value">
                  {formatNumber(cashMovement.amount)}
                </div>
                <div className="wr-register-closure-print-value wr-register-closure-print-value--text">
                  {cashMovement.type}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="vd-pt1 vd-flex vd-flex--justify-between">
          <div>
            <T _str="Cash Payments Received" />
          </div>
          <div className="wr-register-closure-print-values-container wr-register-closure-print-values-container--left">
            <div className="wr-register-closure-print-value">
              {formatNumber(
                data.registerClosure.cashDrawerTotals.cash_sales_total
              )}
            </div>
          </div>
        </div>
        <div className="vd-pt1 vd-flex vd-flex--justify-between">
          <div>
            <T _str="Closing Float" />
          </div>
          <div className="wr-register-closure-print-values-container wr-register-closure-print-values-container--left">
            <div className="wr-register-closure-print-value">
              {data.cashManagement.isCashFloatSet ? (
                formatNumber(data.cashManagement.closingFloat)
              ) : (
                <T _str="Not Set" />
              )}
            </div>
          </div>
        </div>
        <div className="vd-pt1 vd-flex vd-flex--justify-between wr-register-closure-print-cash-to-bank">
          <div>
            <T _str="Cash to Bank" />
          </div>
          <div className="wr-register-closure-print-values-container wr-register-closure-print-values-container--right">
            <div className="wr-register-closure-print-value">
              {formatNumber(data.cashManagement.cashToBank)}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const PrintRegisterClosure: FunctionComponent<PrintRegisterClosureProps> = ({
  data,
}) => {
  const t = useT()
  const paymentManager = usePaymentManager()
  const user = useUserManager().getLoggedInUser()
  const formatNumber = useFormatNumber()

  return (
    <>
      <Header level="1" type="heading">
        <T _str="Register closure summary" />
      </Header>
      <div className="wr-info-field">
        <FieldLabel label={t('Current user')}></FieldLabel>
        <FieldValue>{user.display_name || user.username}</FieldValue>
      </div>
      <div className="register-closure-info-fields">
        <RegisterDetails summary={data.registerClosure} />
      </div>
      <div className="wr-register-closure-print-container">
        <div className="wr-register-closure-print-heading">
          <T _str="Payment types" />
        </div>
        <div className="wr-register-closure-print-values-container">
          <div className="wr-register-closure-print-value">
            <T _str="Expected" />
          </div>
          <div className="wr-register-closure-print-value">
            <T _str="Counted" />
          </div>
        </div>
        <div className="wr-register-closure-print-payments-container">
          {data.registerClosure.payments.map(payment => (
            <Fragment key={payment.payment_type_name}>
              <div className="vd-flex vd-flex--justify-between vd-pt1">
                <div className="wr-register-closure-print-payment-type">
                  <Icon
                    icon="fa-circle"
                    className="wr-register-closure-print-bullet-icon"
                  />
                  <span>{payment.payment_type_name}</span>
                </div>
                <div className="wr-register-closure-print-values-container">
                  <div className="wr-register-closure-print-value">
                    {formatNumber(payment.total)}
                  </div>
                  <div className="wr-register-closure-print-value">
                    {formatNumber(payment.totalCounted)}
                  </div>
                </div>
              </div>
              {data.isCashManagementEnabled &&
              paymentManager.isCashManagementPaymentType(payment) ? (
                <CashMovements data={data} />
              ) : null}
            </Fragment>
          ))}
        </div>
        <div className="wr-register-closure-print-heading">
          <div className="wr-register-closure-print-totals-title">
            <T _str="Totals" />
          </div>
          <div className="wr-register-closure-print-values-container">
            <div className="wr-register-closure-print-value">
              {formatNumber(data.summaryTotals.expectedTotal)}
            </div>
            <div className="wr-register-closure-print-value">
              {formatNumber(data.summaryTotals.countedTotal)}
            </div>
          </div>
        </div>
        {data.registerClosure.note ? (
          <div className="vd-pt3">
            <div className="wr-info-field-label">
              <T _str="Note" />
            </div>
            <div className="wr-info-field-value">
              {data.registerClosure.note}
            </div>
          </div>
        ) : null}
      </div>
    </>
  )
}
